import {
  FC, useEffect, useMemo, 
} from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';

import resetIcon from '/resetIcon.svg';

import {
  cmToFineDry,
  numericValues,
  unMaskInput,
  unMaskInputValue,
} from 'utils/helpers';
import { getClean } from 'hooks/useMaskedInput';
import useCroptimizerMineralization from 'api/hooks/useCroptimizerMineralization';
import MaskInput from 'components/MaskedInput';
import Input from 'components/Input';
import { BoardSteps } from 'utils/constants';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';
import { scrollTop } from 'utils/scroll-top';

import { getDefaultSoPrice, resolveStoredSoPrice, useMineralizationBoard } from './hooks/useMineralizationBoard';

interface Props {
  isPDF?: boolean;
  isGerman: boolean;
}

const MineralizationBoard: FC<Props> = ({ isPDF, isGerman }) => {
  const {
    setActiveTab,
    setStep,
    mineralizationValues,
    setMineralizationValues,
    boardSoilValues,
    boardStatusValues,
    step,
    metricValue,
    kgHa,
    kg,
    isExpertMode,
    isMaintenanceMode,
  } = useCroptimizer();

  const isActive = step === BoardSteps.mineralization;

  const handleFinish = () => {
    setActiveTab(2);
    scrollTop();
  };

  const handleBackStep = () => {
    setStep(BoardSteps.fertilization);
    scrollTop();
  };

  const { t } = useTranslation();
  const {
    cap,
    setCap,
    organicMatter,
    setOrganicMatter,
    cleanorganicmatter,
    errorOrganicMatter,
    setPhAvg,
    cleanPhAvg,
    errorPhAvg,
    errorSoilTillage,
    setSoilTillage,
    soilTillage,
    month,
    setMonth,
    soPrice,
    setSoPrice,
    cleanSoPrice,
  } = useMineralizationBoard(isGerman);

  const { setHandler, setShowBack, setBackHandler, setShowNext, setDisabled } =
    useNextHandler();
  const { data: mineralization } = useCroptimizerMineralization();
  const { currencyGreen } = useCurrency();

  useEffect(() => {
    if (mineralizationValues?.sPrice == null) {
      setSoPrice(getDefaultSoPrice(metricValue));
    }
  }, [metricValue]);

  const getK2Current = useMemo(() => {
    if (boardStatusValues) {
      return String(
        ((0.009 * Number(boardStatusValues?.currentPh) - 0.041) * 100).toFixed(
          2,
        ),
      );
    }
  }, [boardStatusValues]);

  const getK2Target = useMemo(() => {
    if (boardStatusValues) {
      return String(
        ((0.009 * Number(boardStatusValues?.targetPh) - 0.041) * 100).toFixed(2),
      );
    }
  }, [boardStatusValues]);

  const getNGain = useMemo(() => {
    if (
      boardSoilValues &&
      getK2Current &&
      getK2Target &&
      mineralization &&
      organicMatter
    ) {
      const orMatter = Number(cleanorganicmatter) / 100;
      const nrate =
        mineralization?.[0]?.NRate &&
        Number(numericValues(mineralization?.[0]?.NRate)) / 100;
      const result =
      cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))) * 
        10 *
        Number(orMatter) *
        Number(nrate) *
        (Number(getK2Target) - Number(getK2Current));

      return String(Math.round(result));
    }

    return '';
  }, [
    boardSoilValues,
    organicMatter,
    mineralization,
    getK2Target,
    getK2Current,
  ]);

  const getPGain = useMemo(() => {
    if (
      boardSoilValues &&
      getK2Current &&
      getK2Target &&
      mineralization &&
      organicMatter
    ) {
      const orMatter = Number(numericValues(cleanorganicmatter)) / 100;
      const nrate =
        mineralization?.[0]?.NRate &&
        Number(numericValues(mineralization?.[0]?.P2O5Rate)) / 100;
      const result =
      cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))) * 
        10 *
        Number(orMatter) *
        Number(nrate) *
        (Number(getK2Target) - Number(getK2Current));

      return String(Math.round(result));
    }

    return '';
  }, [
    boardSoilValues,
    organicMatter,
    mineralization,
    getK2Target,
    getK2Current,
  ]);

  const getKGain = useMemo(() => {
    if (
      boardSoilValues &&
      getK2Current &&
      getK2Target &&
      mineralization &&
      organicMatter
    ) {
      const orMatter = Number(cleanorganicmatter) / 100;
      const nrate =
        mineralization?.[0]?.NRate &&
        Number(numericValues(mineralization?.[0]?.SO3Rate)) / 100;
      const result =
      cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))) * 
        10 *
        Number(orMatter) *
        Number(nrate) *
        (Number(getK2Target) - Number(getK2Current));

      return String(Math.round(result));
    }

    return '';
  }, [
    boardSoilValues,
    organicMatter,
    mineralization,
    getK2Target,
    getK2Current,
  ]);

  const resetData = () => {
    setSoilTillage('25');
    setPhAvg('0,1');
    setCap(t('no'));
    setMonth('12');
    setOrganicMatter('3');
    setSoPrice('0,5');
  };

  useEffect(() => {
    if (isActive && !mineralizationValues?.cap) {
      setCap(t('no'));
    }
  }, [isActive, mineralizationValues?.cap, setCap, t]);

  useEffect(() => {
    // When active, configure navigation buttons
    // and define the "Next" handler to update board state and trigger status handling
    if (!isActive) {
      return;
    }

    setShowBack(true);
    setShowNext(true);
    setBackHandler(handleBackStep);
    setDisabled(!!errorPhAvg || !!errorOrganicMatter || !!errorSoilTillage);

    setHandler(() => {
      const nrate =
        Number(numericValues(mineralization?.[0]?.NRate ?? '')) / 100;

      const prate =
        Number(numericValues(mineralization?.[0]?.P2O5Rate ?? '')) / 100;

      const srate =
        Number(numericValues(mineralization?.[0]?.SO3Rate ?? '')) / 100;

      setMineralizationValues({
        annualDecrease: cleanPhAvg,
        cap,
        month,
        soilTillage,
        organicMatter: cleanorganicmatter,
        nRate: nrate,
        pRate: prate,
        sRate: srate,
        sPrice: resolveStoredSoPrice(cleanSoPrice),
      });

      handleFinish();
    });
  }, [
    errorPhAvg,
    errorOrganicMatter,
    isActive,
    cleanPhAvg,
    cap,
    month,
    soilTillage,
    cleanorganicmatter,
    cleanSoPrice,
    mineralization,
  ]);

  return (
    <form>
      <div className="border border-primitiveGrey p-8 md:px-4 rounded-2xl">
        <div className="py-8">
          <div className="text-black font-bold text-xl md:text-lg mb-8">
            {t('mineralizationGain')}
          </div>
          <div className="flex mx-[-0.5rem] mb-4 break-inside-avoid">
            <div className="w-1/3 px-2">
              <MaskInput
                titleClassName="top-[-9px] left-[8px] truncate max-w-[85%]"
                title={t('organicMatter')}
                className="w-full"
                value={organicMatter}
                onChange={setOrganicMatter}
                redBorder={!!errorOrganicMatter}
                suffix=" %"
                isBlueBg
              />
              <div className="text-[#FF3B30] text-xs ml-1 mt-1 mt-0 h-2">
                {errorOrganicMatter && <div> {errorOrganicMatter}</div>}
              </div>
            </div>
            {isExpertMode &&
              <>
                <div className="w-1/3 px-2">
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                    title={'K2 ⓘ ' + t('atCurrentPh')}
                    className="w-full"
                    disabled
                    tooltipId="K2 : soil organic matter mineralization coefficient"
                    hasTooltip={
                      'K2 : soil organic matter mineralization coefficient'
                    }
                    suffix=" %"
                    isBlueBg
                    value={unMaskInputValue(`${getK2Current}`)}
                  />
                </div>
                <div className="w-1/3 px-2">
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                    title={'K2 ⓘ ' + t('atTargetPh')}
                    className="w-full"
                    tooltipId="K2 : soil organic matter mineralization coefficient"
                    hasTooltip={
                      'K2 : soil organic matter mineralization coefficient'
                    }
                    isBlueBg
                    disabled
                    suffix=" %"
                    value={unMaskInputValue(`${getK2Target}`)}
                  />
                </div>
              </>
            }
          </div>

          {isExpertMode && 
            <div className="flex mx-[-0.5rem] mb-8 pt-2 break-inside-avoid">
              {isMaintenanceMode ? (
                <div className="w-full px-2">
                  <div className="px-2 py-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 text-sm">
                      Pas de gain de minéralisation NPS calculé si pH actuel = pH visé.
Attention aux impasses, cliquer sur l’onglet l’onglet Strategy « Vue 5 ans » pour consulter les 'Pertes potentielles sur 5 ans sans entretenir le pH'
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-1/3 px-2">
                    <MaskInput
                      titleClassName="top-[-9px] left-[8px] bg-disable-gradient truncate max-w-[85%]"
                      title={'N ' + t('gain') + ` (${kgHa})`}
                      className="w-full"
                      disabled
                      isBlueBg
                      value={unMaskInputValue(getNGain ?? '')}
                    />
                  </div>
                  <div className="w-1/3 px-2">
                    <MaskInput
                      titleClassName="top-[-9px] left-[8px] bg-disable-gradient max-w-[85%]"
                      title={
                        <div className="truncate ">
                          P<sub>2</sub>O<sub>5</sub> {t('gain')} ({kgHa})
                        </div>
                      }
                      className="w-full"
                      disabled
                      isBlueBg
                      value={unMaskInputValue(getPGain ?? '')}
                    />
                  </div>
                  <div className="w-1/3 px-2">
                    <MaskInput
                      titleClassName="top-[-9px] left-[8px] bg-disable-gradient max-w-[85%]"
                      title={
                        <div className="truncate">
                          SO<sub>3</sub> {t('gain')} ({kgHa})
                        </div>
                      }
                      className="w-full"
                      disabled
                      isBlueBg
                      value={unMaskInputValue(getKGain ?? '')}
                    />
                  </div>
                </>
              )}
            </div>
          }

          {isExpertMode &&
            <div className="flex mx-[-0.5rem]">
              <div className="w-1/3 md:w-full px-2">
                <Input
                  titleClassName="top-[-9px] left-[8px] bg-white max-w-[85%]"
                  title={
                    <div className="truncate">
                      SO<sub>3</sub> {t('price')} ({currencyGreen}/{kg})
                    </div>
                  }
                  isBlueBg
                  className="w-full"
                  value={soPrice}
                  onChange={setSoPrice}
                  onBlur={(event) => {
                    const cleaned = getClean(event.target.value).replace(/\.$/, '');

                    if (!cleaned || Number(cleaned) === 0) {
                      setSoPrice('0');
                    }
                  }}
                />
              </div>
            </div>
          }
        </div>

        {!isPDF && (
          <div className="flex items-center justify-center font-medium text-primary md:mb-0">
            <div
              className="cursor-pointer flex items-center"
              onClick={resetData}
            >
              <span className="mr-3">
                <img src={resetIcon} alt="" />
              </span>
              {t('resetData')}
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default MineralizationBoard;
